import { AlertTriangle } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { NumberInput } from "@/components/ui/number-input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useIsMobile } from "@/hooks/use-mobile";
import { formatCurrency, formatCurrencyRounded } from "@/lib/utils";

import type { EditableIngredient } from "./use-production-form";

interface Props {
  ingredients: EditableIngredient[];
  disabled?: boolean;
  onQtyChange: (idx: number, val: string) => void;
}

export function ProductionIngredientTable({
  ingredients,
  disabled,
  onQtyChange,
}: Props) {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <div className="space-y-2.5">
        {ingredients.map((item, idx) => (
          <div
            key={item.rawMaterialId}
            className={`rounded-lg border p-3 text-xs space-y-2.5 ${
              item.isShortage
                ? "border-destructive/40 bg-destructive/5"
                : "bg-card"
            }`}
          >
            {/* Header: Name & SKU */}
            <div className="flex items-start justify-between gap-2">
              <div>
                <span className="font-semibold text-foreground text-xs">
                  {item.rawMaterialName}
                </span>
                {item.rawMaterialSku && (
                  <p className="text-[11px] text-muted-foreground font-mono">
                    {item.rawMaterialSku}
                  </p>
                )}
              </div>
              {item.isShortage && (
                <Badge
                  variant="destructive"
                  className="text-[10px] gap-1 shrink-0"
                >
                  <AlertTriangle className="h-3 w-3" />
                  Stok Bar Kurang
                </Badge>
              )}
            </div>

            {/* Bar Stock & Unit Cost */}
            <div className="flex items-center justify-between text-[11px] bg-muted/30 rounded p-2">
              <span className="text-muted-foreground">Stok Bar Tersedia:</span>
              <span
                className={`font-mono font-medium ${
                  item.isShortage
                    ? "text-destructive font-bold"
                    : "text-foreground"
                }`}
              >
                {item.availableStock.toLocaleString("id-ID")} {item.uomName}
              </span>
            </div>

            {/* Input Kebutuhan */}
            <div className="flex items-center justify-between gap-2 pt-1 border-t border-border/40">
              <span className="font-medium text-xs text-foreground shrink-0">
                Kebutuhan:
              </span>
              <div className="flex items-center gap-1.5 flex-1 max-w-[180px]">
                <NumberInput
                  decimals={4}
                  value={item.qtyStr ? Number(item.qtyStr) : undefined}
                  onChange={(n) => onQtyChange(idx, n ? String(n) : "")}
                  className="h-10 text-xs text-right font-medium"
                  disabled={disabled}
                />
                <span className="text-xs text-muted-foreground font-medium shrink-0">
                  {item.uomName}
                </span>
              </div>
            </div>

            {/* Subtotal Cost */}
            <div className="flex items-center justify-between text-[11px] text-muted-foreground pt-1 border-t border-border/40">
              <span>HPP: {formatCurrency(item.unitCost)}</span>
              <span className="font-mono font-semibold text-xs text-foreground">
                Subtotal: {formatCurrencyRounded(item.subtotalCost)}
              </span>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-md border">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/40">
            <TableHead>Bahan Baku</TableHead>
            <TableHead className="text-right">Stok Bar</TableHead>
            <TableHead className="w-32 text-right">Kebutuhan</TableHead>
            <TableHead className="text-right">HPP Bahan</TableHead>
            <TableHead className="text-right">Subtotal</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {ingredients.map((item, idx) => (
            <TableRow key={item.rawMaterialId}>
              <TableCell>
                <div className="font-medium">{item.rawMaterialName}</div>
                {item.rawMaterialSku && (
                  <div className="text-xs text-muted-foreground">
                    {item.rawMaterialSku}
                  </div>
                )}
              </TableCell>
              <TableCell className="text-right">
                <span
                  className={
                    item.isShortage
                      ? "font-bold text-destructive"
                      : "text-muted-foreground"
                  }
                >
                  {item.availableStock.toLocaleString("id-ID")} {item.uomName}
                </span>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-1">
                  <NumberInput
                    decimals={4}
                    value={item.qtyStr ? Number(item.qtyStr) : undefined}
                    onChange={(n) => onQtyChange(idx, n ? String(n) : "")}
                    className="h-8 w-24 text-right font-medium"
                    disabled={disabled}
                  />
                  <span className="text-xs text-muted-foreground">
                    {item.uomName}
                  </span>
                </div>
              </TableCell>
              <TableCell className="text-right font-mono text-xs">
                {formatCurrency(item.unitCost)}
              </TableCell>
              <TableCell className="text-right font-mono font-medium">
                {formatCurrencyRounded(item.subtotalCost)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
